import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Configuration options for the costs validator.
 */
export interface CostsValidatorOptions {
  /**
   * Name of the form control containing the standard cost value.
   * @default 'academicYearStandardCostTotal'
   */
  standardCostFieldName?: string;

  /**
   * Name of the form control containing the individual cost value.
   * @default 'academicYearIndividualCostTotal'
   */
  individualCostFieldName?: string;

  /**
   * Prefix used for dynamic term cost controls (e.g., 'termCost_' for controls like 'termCost_123').
   * The validator will sum all controls that start with this prefix.
   * @default 'termCost_'
   */
  termCostPrefix?: string;

  /**
   * Error key to use when required fields are missing.
   * @default 'costsRequired'
   */
  requiredErrorKey?: string;

  /**
   * Error key to use when individual cost exceeds standard cost.
   * @default 'individualCostTooHigh'
   */
  tooHighErrorKey?: string;

  /**
   * Error key to use when individual cost does not equal the sum of term costs.
   * @default 'individualCostNotSum'
   */
  notSumErrorKey?: string;
}

/**
 * Validator for cost fields in forms with standard, individual, and term-based costs.
 *
 * Validates:
 * 1. All cost fields (standard, individual, and term costs) are required
 * 2. Individual cost must be less than or equal to standard cost
 * 3. Individual cost must equal the sum of all term costs
 *
 * Validation is skipped only when the individual cost control is disabled
 * (e.g., "Not Attending"). Locked standard cost controls still participate
 * using their loaded value. Disabled term costs are treated as 0.
 *
 * @param options - Configuration object for field names and error keys
 * @returns ValidatorFn that validates cost field relationships
 *
 * @example
 * ```typescript
 * // Use default field names
 * this.form = this.fb.group({
 *   academicYearStandardCostTotal: ['', Validators.required],
 *   academicYearIndividualCostTotal: ['', Validators.required],
 *   termCost_fall: [''],
 *   termCost_spring: ['']
 * }, { validators: costsValidator() });
 *
 * // Custom field names
 * this.form = this.fb.group({
 *   standardCost: ['', Validators.required],
 *   individualCost: ['', Validators.required],
 *   term_fall: [''],
 *   term_spring: ['']
 * }, { validators: costsValidator({
 *   standardCostFieldName: 'standardCost',
 *   individualCostFieldName: 'individualCost',
 *   termCostPrefix: 'term_'
 * })});
 * ```
 */
export function costsValidator(options?: CostsValidatorOptions): ValidatorFn {
  const config = {
    standardCostFieldName: options?.standardCostFieldName ?? 'academicYearStandardCostTotal',
    individualCostFieldName: options?.individualCostFieldName ?? 'academicYearIndividualCostTotal',
    termCostPrefix: options?.termCostPrefix ?? 'termCost_',
    requiredErrorKey: options?.requiredErrorKey ?? 'costsRequired',
    tooHighErrorKey: options?.tooHighErrorKey ?? 'individualCostTooHigh',
    notSumErrorKey: options?.notSumErrorKey ?? 'individualCostNotSum'
  };

  return (group: AbstractControl): ValidationErrors | null => {
    if (!group) {
      return null;
    }

    const individualRaw = group.get(config.individualCostFieldName);
    if (individualRaw?.disabled) {
      return null;
    }

    const standardRaw = group.get(config.standardCostFieldName);
    const standard = readControlValue(standardRaw, true);
    const individual = readControlValue(individualRaw);

    // Sum all dynamic term payment controls.
    // Use group.controls (not group.value) so that disabled controls (prior certified
    // terms) are included — Angular's group.value silently drops disabled fields.
    // Read values directly rather than through readControlValue, which also returns
    // undefined for disabled controls.
    const termControlKeys = Object.keys((group as FormGroup).controls).filter((k) =>
      k.startsWith(config.termCostPrefix)
    );
    const termValues = termControlKeys.map((k) => {
      const ctrl = group.get(k);
      if (!ctrl) return undefined;
      const v = ctrl.value;
      if (v === null || v === undefined || v === '') return ctrl.disabled ? 0 : undefined;
      const n = Number(v);
      return Number.isNaN(n) || !Number.isFinite(n) ? undefined : n;
    });
    const allTermsDefined = termValues.length > 0 && termValues.every((v) => v !== undefined);
    const termSum = termValues.reduce((sum: number, v) => sum + (v ?? 0), 0);

    const errors: ValidationErrors = {};

    // Require all cost fields
    if (!individualRaw?.disabled && validateCostsRequired(standard, individual, termValues)) {
      errors[config.requiredErrorKey] = true;
    }

    const individualCostErrors = validateIndividualCostRules(
      individual,
      standard,
      allTermsDefined,
      termSum,
      config.tooHighErrorKey,
      config.notSumErrorKey
    );
    Object.assign(errors, individualCostErrors);

    return Object.keys(errors).length ? errors : null;
  };
}

/**
 * Helper function to read a numeric value from a control.
 * Returns undefined if the control is disabled (unless includeDisabled = true)
 * or has no value.
 */
function readControlValue(ctrl: AbstractControl | null | undefined, includeDisabled = false): number | undefined {
  if (!ctrl || (ctrl.disabled && !includeDisabled)) return undefined;
  const v = ctrl.value;
  if (v === null || v === undefined || v === '' || v === 0) return undefined;
  const n = Number(v);
  return Number.isNaN(n) || !Number.isFinite(n) ? undefined : n;
}

/**
 * Helper function to validate that all required cost fields have values.
 */
function validateCostsRequired(
  standard: number | undefined,
  individual: number | undefined,
  termValues: (number | undefined)[]
): boolean {
  const anyTermMissing = termValues.includes(undefined);
  return standard === undefined || individual === undefined || (termValues.length > 0 && anyTermMissing);
}

/**
 * Helper function to validate individual cost rules:
 * - Must be <= standard cost
 * - Must equal sum of term costs
 */
function validateIndividualCostRules(
  individual: number | undefined,
  standard: number | undefined,
  allTermsDefined: boolean,
  termSum: number,
  tooHighErrorKey: string,
  notSumErrorKey: string
): ValidationErrors {
  const errors: ValidationErrors = {};
  if (individual === undefined) return errors;

  if (standard !== undefined && individual > standard) {
    errors[tooHighErrorKey] = true;
  }

  if (allTermsDefined && Math.abs(individual - termSum) > 0.01) {
    errors[notSumErrorKey] = true;
  }

  return errors;
}
